// Generates a plausible sample value for a template variable, purely for the admin
// preview (email + SMS) — so the preview shows realistic-looking substituted text
// instead of literal {variableName} braces. Matched by keyword heuristics against the
// variable's key name; falls back to a generic placeholder if nothing matches.

const SAMPLE_NAMES = ['James', 'Priya', 'Michael', 'Aisha', 'Daniel'];
const SAMPLE_LAST_NAMES = ['Smith', 'Sharma', 'Johnson', 'Khan', 'Brown'];

function pick(list, seed) {
  const index = seed.split('').reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % list.length;
  return list[index];
}

export function generateSampleValue(key) {
  const k = key.toLowerCase();

  if (k === 'firstname') return pick(SAMPLE_NAMES, key);
  if (k === 'lastname') return pick(SAMPLE_LAST_NAMES, key);
  if (k === 'username') return 'james.smith';
  if (k.includes('email')) return 'james@example.com';
  if (k.includes('phone')) return '+1 555 0100';
  if (k.includes('url') || k.includes('link')) return 'https://www.tecnogex.com/example';

  if (k.includes('ticketid') || k === 'ticketid') return 'TCK-10234';
  if (k.includes('orderid') || k === 'orderid') return 'ORD-88213';
  if (k.includes('ordernumber')) return 'DES-ORD-88213';
  if (k.includes('subscriptionid')) return 'SUB-55021';
  if (k.includes('subscriptionnumber')) return 'DES-SUB-55021';
  if (k.includes('inquiryid')) return 'INQ-3391';
  if (k.includes('id')) return `ID-${pick(['4471', '8842', '1029', '6653'], key)}`;

  if (k.includes('currencysymbol')) return '$';
  if (k.includes('currencycode') || k === 'currency') return 'USD';
  if (
    k.includes('amount') ||
    k.includes('price') ||
    k.includes('total') ||
    k.includes('subtotal') ||
    k.includes('tax')
  ) {
    return (Number(pick(['1999', '4950', '12000', '299'], key)) / 100).toFixed(2);
  }

  if (k.includes('date') || k.includes('dueat') || k.includes('createdat')) {
    return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  if (k.startsWith('is') || k.startsWith('has') || k.includes('enabled') || k.includes('allowed')) {
    return 'true';
  }

  if (k === 'status' || k.includes('status')) return pick(['Active', 'Confirmed', 'Pending'], key);
  if (k.includes('subject')) return 'Sample subject line';
  if (k.includes('message')) return 'This is a sample message shown here for preview purposes.';
  if (k.includes('reason')) return 'Card declined by issuing bank';
  if (k.includes('name')) return pick(['Desteliq Pro Plan', 'Growth Bundle', 'Starter Pack'], key);
  if (k.includes('code')) return 'SAVE10';

  return `Sample ${key}`;
}

// Builds a { key: sampleValue } object for every variable currently selected on the
// template (the comma-separated availableVariables string), for use as preview data.
export function buildSampleVariables(availableVariablesString) {
  const keys = (availableVariablesString || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

  return keys.reduce((acc, key) => {
    acc[key] = generateSampleValue(key);
    return acc;
  }, {});
}
